import { Modal } from "@material-ui/core";
import { useDashboard } from "MyDashboards/api/useDashboards.hook";
import useDashboardEditor from "MyDashboards/state/editor";
import { Element, Slide } from "MyDashboards/state/editorTypes";
import ProgressDashboardExportModal from "design/components/progress/ProgressDashboardExportModal";
import { toPng } from "html-to-image";
import { t } from "i18next";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import React from "react";
import { createReport, saveBlob } from "reportengine/createReport";
import { DataElement, DataReportRequest } from "reportengine/types";
import * as renderers from "../Render";
import { FileTypes } from "./ExportDashboardTypes";

const createExportData = async (
  pages: Slide[],
  images: Record<string, DataElement>,
  tplName: string,
  outputName: string,
  giveFactor: number,
  onReady?: (data: Blob) => void
) => {
  const factor = pages[0].width / pages[0].tplWidth;

  const data: DataReportRequest = {
    TemplateName: tplName,
    OutputFileName: outputName,
    pages: pages.map((page) => {
      return {
        slideNumber: page.slideNumber,
        elements: page.elements.map((el) => {
          console.log("render el", el);
          console.log("render images ", images);

          if (el.type === "text") {
            return {
              type: "text",
              slideElement: el.tag,
              x: el.x / factor,
              y: el.y / factor,
              width: el.width / factor,
              height: el.height / factor,
              autofit: true,
              paragraphs: el.paragraphs?.filter((p) => {
                return p.portions && p.portions.length > 0;
              }),
            };
          }
          if (el.type === "image") {
            if (images[el.id]) {
              return images[el.id];
            }
            if (el.rendered && el.rendered.pngRef) {
              return {
                type: "image",
                slideElement: el.tag,
                x: el.x / factor,
                y: el.y / factor,
                width: el.width / factor,
                height: el.height / factor,
                autofit: true,
                cachedPng: el.rendered.pngRef,
              };
            }
          }
          return {
            type: "clear",
            slideElement: el.tag,
          };
        }),
      };
    }),
  };

  createReport(data)
    .then((data) => {
      if (onReady) onReady(data);
      // saveBlob(outputName, data);
    })
    .catch((err) => {
      console.error(err);
    });
};

const RenderChartImages = (props: {
  outputName: string;
  onClose?: () => void;
  onReady?: (data: Blob) => void;
}) => {
  const tplName = useDashboardEditor((s) => s.templateName);
  const renderHasStarted = React.useRef<boolean>(false);
  const chartElements = React.useRef<Record<string, HTMLDivElement>>({});
  const chartItems = React.useRef<Record<string, Element>>({});
  const pages = useDashboardEditor((s) => s.pages);
  const factor = useDashboardEditor((s) => s.scaleFactor);
  const waitingChartItems = React.useRef(
    (() => {
      const list: Record<string, boolean> = {};
      pages.forEach((page) => {
        page.elements.forEach((el) => {
          if (el.type === "image" && el.component?.component === "chart") {
            list[el.id] = false;
            chartItems.current[el.id] = el;
          }
        });
      });
      return list;
    })()
  );

  const [readyList, setReadyList] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (Object.keys(waitingChartItems.current).length === 0 && tplName) {
      createExportData(
        pages,
        {},
        tplName,
        props.outputName,
        factor,
        props.onReady
      );
    }
  }, []);

  return (
    <div>
      <h1>OK?</h1>
      {JSON.stringify(readyList)}
      {pages.map((p) => {
        // <props.registry.Render el={el} />
        return (
          <div key={p.id}>
            {p.elements
              .filter(
                (p) => p.type === "image" && p.component?.component === "chart"
              )
              .map((item) => {
                return (
                  <div key={item.id} id={`chartimage_${item.id}`}>
                    Chart ${item.id}
                    <div
                      ref={(newRef) => {
                        if (newRef) {
                          chartElements.current[item.id] = newRef;
                        }
                      }}
                      style={{
                        width: item.width / factor,
                        height: item.height / factor,
                      }}
                    >
                      <renderers.fullScreen.Render
                        el={{
                          ...item,
                          width: item.width / factor,
                          height: item.height / factor,
                        }}
                        onReady={(el, isError?: boolean) => {
                          console.log(
                            "Ready ",
                            el,
                            "Waiting ",
                            waitingChartItems.current
                          );

                          if (isError) {
                            return {
                              type: "clear",
                            };
                          }
                          waitingChartItems.current[el.id] = true;
                          const notReadyList = Object.entries(
                            waitingChartItems.current
                          ).filter(([key, value]) => !value);
                          if (isError) return <div>Error occurred</div>;
                          if (
                            notReadyList.length === 0 &&
                            !renderHasStarted.current
                          ) {
                            renderHasStarted.current = true;

                            console.log("Could render ", chartElements.current);

                            const pngImages = Object.entries(
                              chartElements.current
                            ).map(async ([key, value]) => {
                              /**
                               * Take the PNG value out of the element
                               */
                              const pngData = await toPng(value, {
                                pixelRatio: 1.25,
                                style: { transform: "" },
                              });
                              const start = `data:image/png;base64,`;
                              const left = pngData.slice(start.length);

                              const elem = chartItems.current[key];
                              return {
                                id: key,
                                type: "image",
                                slideElement: elem.tag,
                                value: left,
                                x: elem.x / factor,
                                y: elem.y / factor,
                                width: elem.width / factor,
                                height: elem.height / factor,
                              };
                            });

                            Promise.all(pngImages).then((list) => {
                              console.log("Images", list);
                              const imageElements: Record<string, DataElement> =
                                {};
                              list.forEach((item) => {
                                imageElements[item.id] =
                                  item as unknown as DataElement;
                              });

                              if (tplName) {
                                createExportData(
                                  pages,
                                  imageElements,
                                  tplName,
                                  props.outputName,
                                  factor,
                                  props.onReady
                                );
                              }
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
};
/*
    <div
      style={{
        zIndex: 999999,
        position: 'absolute',
        left: '0px',
        top: '0px',
        width: '100%',
        height: '100%'
      }}
    >
*/
const StartDashboardExport = (props: {
  onClose?: () => void;
  fileType: FileTypes;
}) => {
  const { fileType } = props;
  const pages = useDashboardEditor((s) => s.pages);
  const ctx = useAppContext();
  const { data, isLoading } = useDashboard(ctx.elementId);

  const [blob, setBlob] = React.useState<null | Blob>();

  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const iv = window.setInterval(() => {
      setProgress((value) => {
        const newValue = value + Math.random() * 5;
        if (newValue > 100) {
          window.clearInterval(iv);
          return 100;
        }
        return newValue;
      });
    }, pages.length * 20);
    return () => {
      window.clearInterval(iv);
    };
  }, []);

  if (isLoading) {
    return null;
  }

  return (
    <Modal
      open
      onClose={() => {
        if (props.onClose) {
          props.onClose();
        }
      }}
      BackdropProps={{ style: { opacity: 0.5, background: "#3b7b81" } }}
    >
      <div>
        <ProgressDashboardExportModal
          downloadText={t("Download Report")}
          isOpen
          cancelClickHandler={() => {
            if (props.onClose) {
              props.onClose();
            }
          }}
          completedClickHandler={() => {
            if (blob) {
              saveBlob(`${data?.name}.${fileType}`, blob);
            }
          }}
          isComplete={!!blob}
          progress={progress}
          statusText={`${t("Creating report")}...`}
        />
        <div
          style={{
            position: "absolute",
            left: "-12200px",
            top: "200px",
            width: "2500px",
            height: "2500px",
            background: "white",
            zIndex: 99999,
            color: "black",
          }}
        >
          <RenderChartImages
            outputName={`${data?.name}.${fileType}` || `Dashboard.${fileType}`}
            onReady={(data) => {
              setBlob(data);
              setProgress(100);
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

/*
        <ProgressModal
          downloadText={t('Download Report')}
          cancelClickHandler={() => {
            if (props.onClose) {
              props.onClose();
            }
          }}
          completedClickHandler={() => {

        }}
        isComplete={false}
        progress={20}
        statusText="Creating report..."
      />
*/

/*
<ProgressModal
downloadText={t('Download Report')}
cancelClickHandler={handleCancelClick}
completedClickHandler={handleCompletionClick}
isComplete={!notCompleted}
progress={progress}
statusText={statusText}
/>
*/

export default StartDashboardExport;
